// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * The only part of unfence that changes anything on disk.
 *
 * FOUR MEASURED FACTS SHAPE EVERY LINE OF IT.
 *
 * 1. `{keepExistingData: true}` IS CORRUPTING AND MUST NEVER APPEAR HERE.
 *    Measured on a 27-byte file: `createWritable({keepExistingData:true})`,
 *    write 5 bytes, close → a 27-BYTE FILE reading `XXXXXNAL-CONTENT-1234567890`.
 *    The modal case for this tool is a reply that SHORTENS a file, so under that
 *    flag every such write splices the old tail onto the new content —
 *    silently, and usually still syntactically plausible, so per-hunk review
 *    does not catch it. It is strictly worse than the zero-byte trap it looks
 *    like it prevents.
 *
 * 2. `createWritable()` DOES NOT TRUNCATE THE FILE ON OPEN. The standard: "Any
 *    changes made through stream won't be reflected in the file entry … until
 *    the stream has been closed"; `keepExistingData:false` empties the STREAM's
 *    buffer, not the file. Measured: write-then-throw-and-never-close leaves the
 *    file UNCHANGED. So the invariant is not "wrap it in try/catch" — it is
 *    NEVER CALL close() ON A STREAM YOU DID NOT FULLY WRITE. A `createWritable()`
 *    followed by `close()` with zero writes produces a 0-byte file, and that is
 *    the only way to lose data here.
 *
 * 3. `getFileHandle()` CANNOT TAKE A PATH. Measured:
 *    `root.getFileHandle('src/app.ts', {create:true})` throws
 *    `TypeError: Name is not allowed.` — a TypeError, not a SecurityError, so
 *    the obvious `if (e.name === 'NotAllowedError')` misclassifies it. Every
 *    path is walked segment by segment. And with `{create:true}` on the
 *    intermediates, a hallucinated `srcc/app.ts` silently materialises a new
 *    directory tree that appears in the review as an ordinary new file — so an
 *    intermediate that does not already exist requires the user to say so.
 *
 * 4. THE BACKUP IS A GATE, NOT A GESTURE. It is written, closed, REOPENED and
 *    verified — byte length against `predictLength()`, which is byte-exact, and
 *    the `PK\x05\x06` end-of-central-directory signature at the tail — before
 *    one byte of the user's folder changes. An unverified net is no net.
 */

import { makeZip, predictLength } from 'client-zip';
import { restoreEol } from '../engine/eol';
import { checkPath } from '../engine/pathsafe';
import type { Eol } from '../engine/eol';

export interface WriteItem {
  readonly path: string;
  /** Final text, hunks already applied. Empty string means delete. */
  readonly text: string;
  readonly eol: Eol;
  readonly kind: 'modify' | 'create' | 'delete';
  /** The bytes we planned against, so a change on disk since then is visible. */
  readonly planHash: string;
}

export interface WriteOutcome {
  readonly written: string[];
  readonly skipped: { path: string; reason: string }[];
  readonly failed: { path: string; reason: string }[];
  readonly backupName: string | null;
  readonly backupBytes: number;
}

export type WriteEvent = (level: 'info' | 'good' | 'warn' | 'bad', text: string) => void;

/** A 128-bit content hash. Two FNV-1a passes with different bases. */
export function hashText(text: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x811c9dc5) >>> 0;
  }
  return `${a.toString(16).padStart(8, '0')}${b.toString(16).padStart(8, '0')}`;
}

/**
 * A backup filename that cannot collide.
 *
 * A bare second-resolution stamp collides when two runs land in the same second,
 * and the second run then silently replaces the first — destroying the backup of
 * the run the user is most likely to want to undo.
 */
export function backupName(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-` +
    `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const random = Array.from(crypto.getRandomValues(new Uint8Array(4)))
    .map((b) => 'abcdefghijklmnopqrstuvwxyz234567'[b % 32])
    .join('');
  return `backup-${stamp}-${random}.zip`;
}

export type PermissionState = 'granted' | 'prompt' | 'denied' | 'unsupported';

export async function queryWritePermission(handle: FileSystemDirectoryHandle): Promise<PermissionState> {
  const query = (handle as unknown as { queryPermission?(o: object): Promise<PermissionState> }).queryPermission;
  if (typeof query !== 'function') return 'unsupported';
  return query.call(handle, { mode: 'readwrite' });
}

/** Must be called from a real click — Chromium requires transient activation. */
export async function requestWritePermission(handle: FileSystemDirectoryHandle): Promise<PermissionState> {
  const request = (handle as unknown as { requestPermission?(o: object): Promise<PermissionState> }).requestPermission;
  if (typeof request !== 'function') return 'unsupported';
  try {
    return await request.call(handle, { mode: 'readwrite' });
  } catch {
    return 'denied';
  }
}

/** Resolve a relative path to a file handle, walking one segment at a time. */
async function resolveFile(
  root: FileSystemDirectoryHandle,
  path: string,
  allowNewDirectories: boolean,
): Promise<FileSystemFileHandle> {
  const segments = path.split('/');
  const name = segments.pop();
  if (!name) throw new Error('empty path');
  let dir = root;
  for (const segment of segments) {
    try {
      dir = await dir.getDirectoryHandle(segment, { create: false });
    } catch (error) {
      if (!allowNewDirectories) {
        throw new Error(`the folder "${segment}" does not exist yet`, { cause: error });
      }
      dir = await dir.getDirectoryHandle(segment, { create: true });
    }
  }
  return dir.getFileHandle(name, { create: true });
}

async function readExisting(root: FileSystemDirectoryHandle, path: string): Promise<string | null> {
  const segments = path.split('/');
  const name = segments.pop();
  if (!name) return null;
  let dir = root;
  try {
    for (const segment of segments) dir = await dir.getDirectoryHandle(segment, { create: false });
    const handle = await dir.getFileHandle(name, { create: false });
    return await (await handle.getFile()).text();
  } catch {
    return null;
  }
}

/**
 * PHASE 1 — the backup, written, closed, reopened and verified.
 *
 * The destination handle comes from `showSaveFilePicker`, chosen by the user in
 * their own gesture and OUTSIDE the project folder — writing it inside would
 * mutate the repository the tool is meant to be careful with, and the next
 * outbound bundle would sweep it up.
 *
 * `makeZip` returns a ReadableStream and the destination is a WritableStream, so
 * this is O(1) memory: a 120 MB backup was measured at 6.8 MB of heap.
 */
export async function writeVerifiedBackup(
  destination: FileSystemFileHandle,
  entries: readonly { name: string; input: Uint8Array }[],
): Promise<number> {
  const stamped = entries.map((e) => ({ name: e.name, lastModified: new Date(0), input: e.input }));
  const expected = Number(predictLength(stamped));

  const writable = await destination.createWritable();
  await makeZip(stamped).pipeTo(writable);

  // Reopen and check. `predictLength()` is byte-exact — measured 125,829,448
  // predicted against 125,829,448 streamed — which is what makes this a real
  // verification rather than a plausibility check.
  const written = await destination.getFile();
  if (written.size !== expected) {
    throw new Error(`backup is ${written.size} bytes, expected ${expected} — nothing was written to your folder`);
  }
  const tail = new Uint8Array(await written.slice(Math.max(0, written.size - 22)).arrayBuffer());
  if (!(tail[0] === 0x50 && tail[1] === 0x4b && tail[2] === 0x05 && tail[3] === 0x06)) {
    throw new Error('backup has no end-of-archive marker — nothing was written to your folder');
  }
  return written.size;
}

export interface WriteOptions {
  readonly allowNewDirectories: boolean;
  readonly onEvent?: WriteEvent;
}

/**
 * PHASE 2 — the writes.
 *
 * Sequential and stop-on-error, deliberately. Continuing after a failure means
 * reporting "9 of 12 written" with no idea which state the folder is in; halting
 * means the report names exactly what happened.
 */
export async function writeFiles(
  root: FileSystemDirectoryHandle,
  items: readonly WriteItem[],
  options: WriteOptions,
): Promise<Omit<WriteOutcome, 'backupName' | 'backupBytes'>> {
  const written: string[] = [];
  const skipped: { path: string; reason: string }[] = [];
  const failed: { path: string; reason: string }[] = [];

  for (const item of items) {
    // Belt and braces: the path was checked at plan time, and it is checked
    // again here, because this is the last place before a handle is opened.
    const verdict = checkPath(item.path);
    if (!verdict.ok) {
      skipped.push({ path: item.path, reason: verdict.reason ?? 'refused' });
      continue;
    }

    // THE ONLY DEFENCE AGAINST CLOBBERING WORK DONE DURING REVIEW. The user may
    // have spent five minutes reading a diff while their editor autosaved.
    if (item.kind !== 'create') {
      const current = await readExisting(root, item.path);
      if (current !== null && hashText(current) !== item.planHash) {
        skipped.push({ path: item.path, reason: 'changed on disk while you were reviewing' });
        options.onEvent?.('warn', `${item.path} changed on disk — skipped`);
        continue;
      }
    }

    try {
      if (item.kind === 'delete') {
        const segments = item.path.split('/');
        const name = segments.pop()!;
        let dir = root;
        for (const segment of segments) dir = await dir.getDirectoryHandle(segment, { create: false });
        await dir.removeEntry(name);
        written.push(item.path);
        options.onEvent?.('good', `deleted ${item.path}`);
        continue;
      }

      const handle = await resolveFile(root, item.path, options.allowNewDirectories);
      const bytes = new TextEncoder().encode(restoreEol(item.text, item.eol));

      // The whole payload is buffered BEFORE the stream opens, and close() is
      // reached only after exactly one successful write. A stream that is opened
      // and closed without a write produces a zero-byte file, and that is the
      // one way this function could destroy something.
      const writable = await handle.createWritable();
      try {
        await writable.write(bytes);
      } catch (error) {
        await writable.abort?.();
        throw error;
      }
      await writable.close();

      written.push(item.path);
      options.onEvent?.('good', `wrote ${item.path} (${bytes.length} bytes)`);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      failed.push({ path: item.path, reason });
      options.onEvent?.('bad', `failed on ${item.path}: ${reason}`);
      // Stop the batch. A half-applied set the user cannot name is worse than a
      // named failure they can act on.
      break;
    }
  }

  return { written, skipped, failed };
}

/**
 * Which intermediate directories do not exist yet.
 *
 * Asked BEFORE anything is written, because `{create:true}` on the walk turns a
 * misspelling — `srcc/app.ts`, `src/componets/Foo.tsx` — into a brand-new tree
 * that looks, in the review, exactly like an ordinary new file.
 */
export async function missingDirectories(
  root: FileSystemDirectoryHandle,
  paths: readonly string[],
): Promise<string[]> {
  const missing = new Set<string>();
  for (const path of paths) {
    const segments = path.split('/');
    segments.pop();
    let dir: FileSystemDirectoryHandle | null = root;
    const walked: string[] = [];
    for (const segment of segments) {
      walked.push(segment);
      if (!dir) {
        missing.add(walked.join('/'));
        continue;
      }
      try {
        dir = await dir.getDirectoryHandle(segment, { create: false });
      } catch {
        missing.add(walked.join('/'));
        dir = null;
      }
    }
  }
  return [...missing].sort();
}
